import type {
  YMarketOrder,
  YMarketOrdersFreshness,
  YMarketOrdersPage,
  YMarketOrdersResponse,
  YMarketOrdersStaleSource,
  YMarketBuyer,
} from '@/lib/types'
import type {
  UpdateOrderStateInput,
  GetOrderStateResult,
  TransitDaysInput,
} from '../schemas/orders.schema'
import type { OrdersQueryParamsType } from '../schemas/ymarket.schema'
import { ApiResponseError, apiFetch, readApiJson } from '@/lib/api-fetch'
import {
  isCompleteMissionControlSummary,
  type AuthoritativeMissionControlSummary,
} from '@/lib/order-mission-control'

export interface CdekPrintDocumentLink {
  mediaType: 'application/pdf'
  pageCount: number
  widthPoints: number
  heightPoints: number
  url: string
}

export interface CdekPrintBundle {
  orderId: number
  a6: CdekPrintDocumentLink
  a4: CdekPrintDocumentLink
}

export interface YandexPrintDocumentLink {
  mediaType: 'application/pdf'
  pageCount: number | null
  widthPoints: number
  heightPoints: number
  url: string
}

export interface YandexPrintBundle {
  orderId: number
  a6: YandexPrintDocumentLink
}

export interface PackOrderResult {
  orderId: number
  state: 'packed'
  outcome: 'packed'
  idempotencyKey: string
  replayed: boolean
}

export const ORDERS_FRESHNESS_HEADER = 'X-Orders-Freshness'

function staleSource(value: unknown): YMarketOrdersStaleSource | null {
  return value === 'provider_cache' || value === 'database' ? value : null
}

/**
 * Reads the freshness the server stated. Stale wins over fresh wherever either
 * the body or the header says so: a stale page must never render as current.
 */
export function parseOrdersFreshness(freshness: unknown, header: string | null): YMarketOrdersFreshness {
  const body = typeof freshness === 'object' && freshness !== null ? freshness : null
  const bodyState = body !== null && 'state' in body ? body.state : undefined
  if (bodyState !== 'stale' && header !== 'stale') return { state: 'fresh' }
  const source = body !== null && 'source' in body ? staleSource(body.source) : null
  const rawObservedAt = body !== null && 'observedAt' in body ? body.observedAt : undefined
  const observedAt = typeof rawObservedAt === 'string' && Number.isFinite(Date.parse(rawObservedAt))
    ? rawObservedAt
    : null
  return { state: 'stale', source, observedAt }
}

export async function fetchOrders(params: OrdersQueryParamsType & { fresh?: boolean }): Promise<YMarketOrdersPage> {
  const query = new URLSearchParams()
  if (params.limit) query.set('limit', params.limit)
  if (params.status) query.set('status', params.status)
  if (params.pageToken) query.set('pageToken', params.pageToken)
  if (params.orderId) query.set('orderId', params.orderId)
  if (params.fresh) query.set('fresh', '1')
  const res = await apiFetch(`/api/ymarket/orders?${query}`)
  const data = await readApiJson<YMarketOrdersResponse>(res)
  return {
    orders: data.orders,
    paging: data.paging,
    freshness: parseOrdersFreshness(data.freshness, res.headers.get(ORDERS_FRESHNESS_HEADER)),
  }
}

export async function fetchMissionControl(): Promise<AuthoritativeMissionControlSummary> {
  const res = await apiFetch('/api/orders/mission-control')
  const data = await readApiJson<unknown>(res)
  if (!isCompleteMissionControlSummary(data)) {
    throw new Error('Полная сводка доставки временно недоступна.')
  }
  return data
}

/** Fetches one order independently from the current list filters. */
export async function fetchOrderById(orderId: number): Promise<YMarketOrder | null> {
  const result = await fetchOrders({ orderId: String(orderId) })
  return result.orders.find((order) => order.orderId === orderId) || null
}

export async function fetchBuyer(
  campaignId: number,
  orderId: number
): Promise<{ buyer: YMarketBuyer | null }> {
  const res = await apiFetch(`/api/ymarket/orders/${campaignId}/${orderId}/buyer`)
  return readApiJson<{ buyer: YMarketBuyer | null }>(res)
}

export async function getOrderState(orderId: number): Promise<GetOrderStateResult> {
  const res = await apiFetch(`/api/orders/${orderId}/state`)
  return readApiJson<GetOrderStateResult>(res)
}

export async function markManualMessage(orderId: number, kind: 'firstMsg' | 'confirmMsg' | 'courierMsg'): Promise<{ messageKind: string; markedAt: string }> {
  const res = await apiFetch(`/api/orders/${orderId}/messages/${kind}/manual-mark`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  return readApiJson<{ messageKind: string; markedAt: string }>(res)
}

export async function unmarkManualMessage(orderId: number, kind: 'firstMsg' | 'confirmMsg' | 'courierMsg'): Promise<void> {
  const res = await apiFetch(`/api/orders/${orderId}/messages/${kind}/manual-mark`, { method: 'DELETE' })
  if (!res.ok) await readApiJson(res)
}

export async function updateOrderState(
  orderId: number,
  body: UpdateOrderStateInput
): Promise<void> {
  const res = await apiFetch(`/api/orders/${orderId}/state`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    await readApiJson(res)
  }
}

export async function updateTransitDays(input: TransitDaysInput): Promise<void> {
  const res = await apiFetch('/api/transit-days', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    await readApiJson(res)
  }
}

/** A print refusal that names the Product fields blocking the receipt page. */
export class CdekPrintRefusalError extends ApiResponseError {
  readonly missingFieldLabels: readonly string[]

  constructor(status: number, message: string, missingFieldLabels: readonly string[]) {
    super(status, message)
    this.name = 'CdekPrintRefusalError'
    this.missingFieldLabels = missingFieldLabels
  }
}

/** A Market print refusal with the same Product-field detail contract. */
export class YandexPrintRefusalError extends ApiResponseError {
  readonly missingFieldLabels: readonly string[]

  constructor(status: number, message: string, missingFieldLabels: readonly string[]) {
    super(status, message)
    this.name = 'YandexPrintRefusalError'
    this.missingFieldLabels = missingFieldLabels
  }
}

async function refusalFieldLabels(response: Response): Promise<string[]> {
  try {
    const payload: unknown = await response.json()
    if (typeof payload !== 'object' || payload === null || !('missingFieldLabels' in payload)) return []
    const labels = payload.missingFieldLabels
    if (!Array.isArray(labels)) return []
    return labels.filter(
      (label): label is string =>
        typeof label === 'string' && label.length > 0 && label.length <= 60 && /[А-Яа-яЁё]/.test(label),
    )
  } catch {
    return []
  }
}

export async function fetchCdekPrintDocuments(orderId: number): Promise<CdekPrintBundle> {
  const res = await apiFetch(`/api/cdek/orders/${orderId}/print`)
  if (res.ok) return readApiJson<CdekPrintBundle>(res)
  const labels = await refusalFieldLabels(res.clone())
  try {
    return await readApiJson<CdekPrintBundle>(res)
  } catch (caught) {
    if (caught instanceof ApiResponseError && labels.length > 0) {
      throw new CdekPrintRefusalError(caught.status, caught.message, labels)
    }
    throw caught
  }
}

export async function fetchCdekPrintPdf(url: string): Promise<Blob> {
  const res = await apiFetch(url)
  if (!res.ok) {
    await readApiJson(res)
  }
  const contentType = res.headers.get('content-type')?.split(';', 1)[0].trim().toLowerCase()
  if (contentType !== 'application/pdf') {
    throw new Error('Документ СДЭК имеет неверный формат.')
  }
  return res.blob()
}

export async function fetchYandexPrintDocuments(orderId: number): Promise<YandexPrintBundle> {
  const res = await apiFetch(`/api/ymarket/orders/${orderId}/print`)
  if (res.ok) return readApiJson<YandexPrintBundle>(res)
  const labels = await refusalFieldLabels(res.clone())
  try {
    return await readApiJson<YandexPrintBundle>(res)
  } catch (caught) {
    if (caught instanceof ApiResponseError && labels.length > 0) {
      throw new YandexPrintRefusalError(caught.status, caught.message, labels)
    }
    throw caught
  }
}

export async function fetchYandexPrintPdf(url: string): Promise<Blob> {
  const res = await apiFetch(url)
  if (!res.ok) {
    await readApiJson(res)
  }
  const contentType = res.headers.get('content-type')?.split(';', 1)[0].trim().toLowerCase()
  if (contentType !== 'application/pdf') {
    throw new Error('Документ Маркета имеет неверный формат.')
  }
  return res.blob()
}

export async function packOrder(orderId: number, idempotencyKey: string): Promise<PackOrderResult> {
  const res = await apiFetch(`/api/orders/${orderId}/pack`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idempotencyKey }),
  })
  return readApiJson<PackOrderResult>(res)
}
